/**
 * Formatting utility functions
 */

import { RunState, Difficulty } from '../types';

export function formatTimeRemaining(expiresAt: string): string {
  const now = new Date();
  const expiry = new Date(expiresAt);
  const diff = expiry.getTime() - now.getTime();

  if (diff <= 0) {
    return 'Expired';
  }

  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
}

export function formatDuration(minutes: number): string {
  if (minutes < 60) {
    return `${minutes}m`;
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
}

export function formatDateTime(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleString();
}

export function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString();
}

export function formatTime(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleTimeString();
}

export function formatRelativeTime(dateString: string): string {
  const now = new Date();
  const date = new Date(dateString);
  const diff = now.getTime() - date.getTime();

  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) {
    return `${days} day${days > 1 ? 's' : ''} ago`;
  }
  if (hours > 0) {
    return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  }
  if (minutes > 0) {
    return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
  }
  return 'Just now';
}

export function getRunStateColor(state: RunState): string {
  switch (state) {
    case RunState.PENDING:
      return 'text-yellow-400';
    case RunState.PROVISIONING:
      return 'text-blue-400';
    case RunState.ACTIVE:
      return 'text-green-400';
    case RunState.DESTROYING:
      return 'text-orange-400';
    case RunState.DESTROYED:
      return 'text-gray-400';
    case RunState.FAILED:
      return 'text-red-400';
    default:
      return 'text-gray-400';
  }
}

export function getRunStateBackground(state: RunState): string {
  switch (state) {
    case RunState.PENDING:
      return 'bg-yellow-900 border-yellow-700';
    case RunState.PROVISIONING:
      return 'bg-blue-900 border-blue-700';
    case RunState.ACTIVE:
      return 'bg-green-900 border-green-700';
    case RunState.DESTROYING:
      return 'bg-orange-900 border-orange-700';
    case RunState.DESTROYED:
      return 'bg-gray-900 border-gray-700';
    case RunState.FAILED:
      return 'bg-red-900 border-red-700';
    default:
      return 'bg-gray-900 border-gray-700';
  }
}

export function getDifficultyColor(difficulty: Difficulty): string {
  switch (difficulty) {
    case Difficulty.BEGINNER:
      return 'text-green-400 bg-green-900 border-green-700';
    case Difficulty.INTERMEDIATE:
      return 'text-yellow-400 bg-yellow-900 border-yellow-700';
    case Difficulty.ADVANCED:
      return 'text-red-400 bg-red-900 border-red-700';
    default:
      return 'text-gray-400 bg-gray-900 border-gray-700';
  }
}

export function formatPercentage(value: number, total: number): string {
  if (total === 0) return '0%';
  return `${Math.round((value / total) * 100)}%`;
}

export function formatScore(score: number, total: number): string {
  return `${score}/${total} (${formatPercentage(score, total)})`;
}